import { useContext, useMemo } from 'react';

import type { Features } from '../../../../common/constants/bootstrap';
import type { PrimaryProductKey } from '../../../../common/constants/products';
import type { BootstrapVariables } from '../../../../common/constants/relay/types';
import type { UserDetailsWithStatus } from '../../../../common/constants/types';
import { getUser } from '../../../../common/utils/bootstrap';
import { serveBootstrapProviderExp } from '../../../../common/utils/experiments';
import {
	useBootstrapStateStore,
	useCloudIdStore,
	useFeaturesStore,
	useOrgIdStore,
	usePrimaryProductStore,
	useUserStore,
} from '../../bootstrap';
import { DynamicBootstrapContext, StaticBootstrapContext } from '../bootstrap-context-provider';

type HookReturn<T> = (T)[]

/**
 * To migrate each accessor safely (single PR) without modifying existing code sites:
 * 1. Identify accessor e.g. `useFeatures`
 * 2. In this file ./accessors:
 * 		a. Create context version of hook in this file with EXP check, e.g. `useFeatures`
 * 		b. For the false side of the codepath, use `useFeaturesStore` from bootstrap/index (the existing store version which is renamed below in step 2a)
 * 		c. (optional) Create a non-exported subfunction if the logic is complex, e.g. see useBootstrapStateContext's handling of callbacks with a merged store
 * 3. In ../../bootstrap
 * 		a. 	Add "Store" suffix to hook and a deprecated JSDOC, e.g. `useFeatures` -> `useFeaturesStore`
 * 4. in ../../index.tsx
 *		a. Remove barrel file export to reduce code path ambiguity
 * 5. Fix imports in code sites (including tests/mocking) to import from bootstrap/accessors instead of bootstrap/index (and not the barrel file)
 *		a. For cases from ../../boostrap simply add accessors. e.g. from '../../../controllers/store/bootstrap' ->from '../../../controllers/store/bootstrap';
 *		b. For cases from ../../index.tsx simply change to accessors. e.g. from '../../../controllers/store' -> '../../../controllers/store/bootstrap/accessors';
 *		This should be the *only* code change required in code sites
 * Now this code path is safe to migrate via FG rollout, with minimal changes in code sites!
 *
 * Note: the barrel file removals are due to eslint-error from @repo/internal/import/no-relative-barrel-file-imports
 *
 * Later cleanup (single PR):
 * 1. Remove the serveBootstrapContext=false code path from the context version in this file (from step 2)
 * 2. Delete the "Store" suffix code entirely (from step 3) from bootstrap/index (the whole store should be removable)
 *
 * At the end of this migration we'll also likely drop the `bootstrap` path segment entirely.
 */

const useBootstrapStateContext = (cb: (state: BootstrapVariables) => BootstrapVariables) => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);
	const context = useMemo(() => {
		return cb({ ...staticContext, referrer: dynamicContext?.referrer });
	}, [staticContext, dynamicContext.referrer, cb]);

	return context;
};

/**
 * Retrieve BootstrapVariables (with applied callback) from the store. Should only be used at the toplevel of relay as this combines statid and dynamic props and may rerender.
 */
export const useBootstrapState = (cb: (state: BootstrapVariables) => BootstrapVariables) => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useBootstrapStateContext(cb) : useBootstrapStateStore(cb);
};

/**
 * Retrieve features from the store. Features are defined by the host product and allow fg/experiment service outside our package.
 */
export const useFeatures = (): HookReturn<Features | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useFeaturesStore();

	return serveContext
		? [staticContext.features]
		: [fromStore] satisfies HookReturn<Features | undefined>; // HookReturnValue from RSS satisfies this, but we ignore the rest of it
};

/**
 * Retrieve cloudId from the store. CloudId identifies the Atlassian cloud instance.
 */
export const useCloudId = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useCloudIdStore();

	return serveContext
		? [staticContext.cloudId]
		: [fromStore] satisfies HookReturn<string | undefined>;
};

/**
 * Retrieve orgId from the store. OrgId identifies the Atlassian organization.
 */
export const useOrgId = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useOrgIdStore();

	return serveContext
		? [staticContext.orgId]
		: [fromStore] satisfies HookReturn<string | undefined>;
};

/**
 * Retrieve primaryProduct from the store. PrimaryProduct identifies the main product context.
 */
export const usePrimaryProduct = (): HookReturn<PrimaryProductKey | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = usePrimaryProductStore();

	return serveContext
		? [staticContext.primaryProduct]
		: [fromStore] satisfies HookReturn<PrimaryProductKey | undefined>;
};

/**
 * Retrieve user details from the store with authentication status.
 */
export const useUser = (): HookReturn<UserDetailsWithStatus> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useUserStore();

	// Apply getUser transformation for context path (store already applies it in selector)
	return serveContext
		? [getUser(staticContext.user)]
		: [fromStore] satisfies HookReturn<UserDetailsWithStatus>;
};
